import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "container-fluid admin-page" }
const _hoisted_2 = { class: "title-area" }
const _hoisted_3 = {
  key: 0,
  class: "create-btn"
}
const _hoisted_4 = ["innerHTML"]

import group2Api from 'src/apis/group2';
import group3Api from 'src/apis/group3';
import Group3Table from 'src/components/Admin/Group3/Group3Table.vue';
import Group3EditModal from 'src/components/Admin/Group3/Group3EditModal.vue';
import type { AdminGroup2 } from 'src/models/group2';
import type { AdminGroup3 } from 'src/models/group3';
import type { Group3InputModel } from 'src/models/group3';
import { createGroup3InitInputModel } from 'src/models/group3';
import type { AdminGroup3Params } from 'src/models/apis/group3/adminGroup3Request';
import {
  computed,
  onMounted,
  reactive,
} from 'vue';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import type { AxiosError } from 'axios';
import { useAuth } from 'src/composables/useAuth';

const DEFAULT_ERROR_MSG = '不明なエラーです。ネットワークの状態をご確認いただき再度お試し下さい。';

interface Groups3PageState {
  groups3: AdminGroup3[];
  groups2: AdminGroup2[];
  editingGroup3: Group3InputModel;
  showEditModal: boolean;
  isEditMode: boolean;
  isRequesting: boolean;
  showErrorModal: boolean;
  errorModalMsg: string;
  httpErrorMsgs: string[];
}

const errorModalTitle = 'エラー';

export default /*@__PURE__*/_defineComponent({
  __name: 'Groups3',
  setup(__props) {

const state = reactive<Groups3PageState>({
  groups3: [],
  groups2: [],
  editingGroup3: createGroup3InitInputModel(),
  showEditModal: false,
  isEditMode: false,
  isRequesting: false,
  showErrorModal: false,
  errorModalMsg: DEFAULT_ERROR_MSG,
  httpErrorMsgs: [],
});

const { user } = useAuth();
const canCreateGroup3 = computed(() => {
  return user?.hasRoleBranchAdmin ?? false;
});

const loadGroups3 = async() => {
  const groups3 = await group3Api.adminIndex();
  if (!groups3) return;

  state.groups3 = groups3;
};

const nextDispOrder = (): number => {
  let maxDispOrder = 0;
  state.groups3.forEach(g3 => {
    if (g3.dispOrder && !isNaN(g3.dispOrder) && maxDispOrder < g3.dispOrder) {
      maxDispOrder = g3.dispOrder;
    }
  });
  return maxDispOrder + 1;
};

const openCreateModal = (): void => {
  state.isEditMode = false;
  state.editingGroup3 = {
    ...createGroup3InitInputModel(),
    dispOrder: nextDispOrder(),
  };
  state.httpErrorMsgs = [];
  state.showEditModal = true;
};

const openUpdateModal = (group3: AdminGroup3): void => {
  state.isEditMode = true;
  state.editingGroup3 = {
    id: group3.id,
    name: group3.name,
    parentId: group3.parentId,
    dispOrder: group3.dispOrder,
  };
  state.httpErrorMsgs = [];
  state.showEditModal = true;
};

const onCreateGroup3 = async(payload: AdminGroup3Params): Promise<void> => {
  state.isRequesting = true;
  try {
    await group3Api.adminCreate(payload);
    state.httpErrorMsgs = [];
    await loadGroups3();
    state.showEditModal = false;
  } catch (err) {
    state.httpErrorMsgs = getErrorMessages(err as AxiosError);
  } finally {
    state.isRequesting = false;
  }
};

const onUpdateGroup3 = async(id: number, payload: AdminGroup3Params): Promise<void> => {
  state.isRequesting = true;
  try {
    await group3Api.adminUpdate(id, payload);
    state.httpErrorMsgs = [];
    await loadGroups3();
    state.showEditModal = false;
  } catch (err) {
    state.httpErrorMsgs = getErrorMessages(err as AxiosError);
  } finally {
    state.isRequesting = false;
  }
};

const onDestroyGroup3 = async(group3: AdminGroup3): Promise<void> => {
  try {
    await group3Api.adminDestroy(group3.id);
    await loadGroups3();
  } catch (err) {
    const errMsgs = getErrorMessages(err as AxiosError);
    if (errMsgs.includes('has users')) {
      state.errorModalMsg = '1名以上のユーザーが所属しているので削除できません。';
    } else if (errMsgs.includes('has devices')) {
      state.errorModalMsg = '1件以上の車載器が所属しているので削除できません。';
    } else {
      state.errorModalMsg = DEFAULT_ERROR_MSG;
    }
    state.showErrorModal = true;
  }
};

onMounted(async() => {
  state.groups2 = await group2Api.adminIndex();
  await loadGroups3();
});


return (_ctx: any,_cache: any) => {
  const _component_Modal = _resolveComponent("Modal")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[3] || (_cache[3] = _createElementVNode("h2", null, "部署管理", -1)),
      (canCreateGroup3.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("button", {
              class: "btn btn-info",
              onClick: openCreateModal
            }, [...(_cache[2] || (_cache[2] = [
              _createElementVNode("i", { class: "fas fa-plus" }, null, -1),
              _createElementVNode("span", { class: "ml-1" }, "新規作成", -1)
            ]))])
          ]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(Group3Table, {
      groups3: state.groups3,
      groups2: state.groups2,
      onEdit: openUpdateModal,
      onDestroy: onDestroyGroup3,
      onSearch: loadGroups3
    }, null, 8, ["groups3", "groups2"]),
    (state.showEditModal)
      ? (_openBlock(), _createBlock(Group3EditModal, {
          key: 0,
          "is-edit-mode": state.isEditMode,
          group3: state.editingGroup3,
          groups2: state.groups2,
          "is-requesting": state.isRequesting,
          "http-error-msgs": state.httpErrorMsgs,
          onCreate: onCreateGroup3,
          onUpdate: onUpdateGroup3,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (state.showEditModal = false))
        }, null, 8, ["is-edit-mode", "group3", "groups2", "is-requesting", "http-error-msgs"]))
      : _createCommentVNode("", true),
    (state.showErrorModal)
      ? (_openBlock(), _createBlock(_component_Modal, {
          key: 1,
          title: errorModalTitle,
          "show-bottom-dismiss-button": false,
          onClose: _cache[1] || (_cache[1] = ($event: any) => (state.showErrorModal = false))
        }, {
          body: _withCtx(() => [
            _createElementVNode("div", null, [
              _createElementVNode("p", {
                class: "p2 font-mtx-desc",
                innerHTML: state.errorModalMsg
              }, null, 8, _hoisted_4)
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})